import { useState, useEffect, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import { searchForProviderAvailability } from '../../../store/new-visit/NewVisitThunk'
import { filterProviders } from '../helper'
import { TIME_FOR_DATE_RANGE_REQUEST_FROM, TIME_FOR_DATE_RANGE_REQUEST_TO } from '../../../utils/dateUtils'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { type QuickMdState } from '@/app/store'

type UseProviderSearchParams = {
  bookingId: string
  // The API models these ids as nullable (`string | null` in the generated schema), so accept null
  // rather than pretending callers have already narrowed them.
  visitTypeId?: string | null
  patientId?: string | null
  date: string
  filterPastVisits: boolean
  time: string
  initialProviderName?: string
  timeZone: string
  /**
   * VisitId of the visit being rescheduled — forwarded to the availability search so the backend
   * can restrict slots to the visit's treatment center. Absent outside the reschedule flow.
   */
  rescheduleVisitId?: string | null | undefined
}

const selectSearchResults = (bookingId: string) => (state: QuickMdState) =>
  state.newVisitReducer.visits[bookingId]?.searchResultsProviders?.results

const selectIsSearching = (bookingId: string) => (state: QuickMdState) =>
  Boolean(state.newVisitReducer.visits[bookingId]?.loading)

export const useProviderSearch = ({
  bookingId,
  visitTypeId,
  patientId,
  date,
  filterPastVisits,
  time,
  initialProviderName,
  timeZone,
  rescheduleVisitId,
}: UseProviderSearchParams) => {
  const dispatch = useAppDispatch()
  const emptyResults = useRef([])

  // State for search and filtered results
  const [searchText, setSearchText] = useState(initialProviderName)

  const [filteredResults, setFilteredResults] = useState([])

  const buildSearchFilters = useCallback(
    () => ({
      fromDateTime: moment(date).format(TIME_FOR_DATE_RANGE_REQUEST_FROM),
      toDateTime: moment(date).format(TIME_FOR_DATE_RANGE_REQUEST_TO),
      timeZone,
      visitTypeId: visitTypeId || '',
      patientId: patientId || '',
      bookingId,
      rescheduleVisitId,
    }),
    [date, timeZone, visitTypeId, patientId, bookingId, rescheduleVisitId],
  )

  useEffect(() => {
    const fetchProviders = async () => {
      const searchFilters = buildSearchFilters()
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      await dispatch(searchForProviderAvailability({ ...searchFilters }))
    }

    fetchProviders()
  }, [dispatch, buildSearchFilters])

  // Redux selector for API results. The empty-array fallback stays outside the selector so the
  // reference is stable across renders and doesn't retrigger the filter effect below.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- `results` is `any`: the reducer stores whatever `apiCall` parsed out of the response body, so a falsy non-nullish value is not ruled out. `||` sends every falsy value to the empty-array fallback; `??` would forward `0`/`false`/`''` into `filterProviders`, which calls `.filter` on it and throws. Keeping `||` preserves the long-standing behavior.
  const searchResults = useAppSelector(selectSearchResults(bookingId)) || emptyResults.current

  // The reducer already tracks this per booking; the hook just never surfaced it, so callers were
  // reading an always-undefined `isLoading` and their loading UI never rendered.
  const isLoading = useAppSelector(selectIsSearching(bookingId))

  useEffect(() => {
    const filtered = filterProviders(searchResults, searchText, filterPastVisits, time)
    setFilteredResults(filtered)
  }, [searchResults, searchText, filterPastVisits, time])

  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const onSearchInputChange = useCallback((value) => {
    setSearchText(value)
  }, [])

  return {
    searchText,
    filteredResults,
    onSearchInputChange,
    isLoading,
  }
}
